<template>
  <div class="layout-container" :class="{ 'is-mobile': isMobile }">
    <!-- 手机端遮罩 -->
    <div v-if="isMobile && sidebar.opened" class="sidebar-mask" @click="closeSidebar" />

    <!-- 侧边栏 -->
    <div class="sidebar-container" :class="{ 'sidebar-collapsed': !sidebar.opened }">
      <Sidebar />
    </div>

    <!-- 主内容区 -->
    <div class="main-container" :class="{ 'main-collapsed': !sidebar.opened }">
      <!-- 顶部导航栏 -->
      <div class="navbar">
        <Navbar />
      </div>

      <!-- 页签栏 -->
      <TabsBar />

      <!-- 内容区域 -->
      <div class="app-main">
        <router-view />
      </div>
    </div>

    <!-- 全局水印 -->
    <div class="watermark" :style="watermarkStyle"></div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Sidebar from "./Sidebar/index.vue";
import Navbar from "./Navbar/index.vue";
import TabsBar from "./TabsBar/index.vue";

const MOBILE_WIDTH = 768;

export default {
  name: "LayoutComponent",
  components: {
    Sidebar,
    Navbar,
    TabsBar,
  },
  computed: {
    ...mapGetters("app", ["sidebar", "device"]),
    isMobile() {
      return this.device === "mobile";
    },
    watermarkStyle() {
      const user = this.$store.state.user.userInfo;
      const text = user.nickname || user.username || user.phone || user.name || '';
      if (!text) return { display: 'none' };
      return { backgroundImage: `url(${this._genWatermark(text)})` };
    },
  },
  methods: {
    // 静默刷新用户权限，不弹错误提示
    async _refreshPermissions() {
      try {
        await this.$store.dispatch('user/getUserInfo');
      } catch (e) {
        // token 失效时 axios 拦截器会跳转登录，这里静默忽略即可
      }
    },
    _genWatermark(text) {
      const canvas = document.createElement('canvas');
      canvas.width = 240;
      canvas.height = 160;
      const ctx = canvas.getContext('2d');
      ctx.rotate(-22 * Math.PI / 180);
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.textAlign = 'center';
      ctx.fillText(text, 80, 120);
      return canvas.toDataURL();
    },
    closeSidebar() {
      this.$store.dispatch("app/closeSideBar", { withoutAnimation: false });
    },
    _checkDevice() {
      const isMobile = window.innerWidth < MOBILE_WIDTH;
      const wasMobile = this.device === "mobile";
      this.$store.dispatch("app/toggleDevice", isMobile ? "mobile" : "desktop");
      if (isMobile && this.sidebar.opened) {
        // 手机端自动收起
        this.$store.dispatch("app/closeSideBar", { withoutAnimation: true });
      } else if (!isMobile && wasMobile && !this.sidebar.opened) {
        // 从手机切回电脑端，自动展开
        this.$store.dispatch("app/toggleSideBar");
      }
    },
  },
  watch: {
    $route(to) {
      this.$store.commit("tabs/OPEN", {
        path: to.path,
        title: to.meta?.title || to.name || to.path,
        affix: !!to.meta?.affix,
      });
      // 手机端路由切换后自动收起侧边栏
      if (this.isMobile && this.sidebar.opened) {
        this.$store.dispatch("app/closeSideBar", { withoutAnimation: false });
      }
    },
  },
  created() {
    // 初始化当前路由的页签
    this.$store.commit("tabs/OPEN", {
      path: this.$route.path,
      title: this.$route.meta?.title || this.$route.name || this.$route.path,
      affix: !!this.$route.meta?.affix,
    });
  },
  mounted() {
    this._checkDevice();
    this._resizeHandler = () => this._checkDevice();
    window.addEventListener("resize", this._resizeHandler);

    // 定时静默刷新权限（每 3 分钟）
    this._permRefreshTimer = setInterval(() => {
      this._refreshPermissions();
    }, 3 * 60 * 1000);

    // 页面重新可见时刷新权限（切 tab 后回来）
    this._visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this._refreshPermissions();
      }
    };
    document.addEventListener('visibilitychange', this._visibilityHandler);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._resizeHandler);
    clearInterval(this._permRefreshTimer);
    document.removeEventListener('visibilitychange', this._visibilityHandler);
  },
};
</script>

<style lang="scss" scoped>
.layout-container {
  display: flex;
  height: 100vh;
}

.sidebar-container {
  width: $sidebar-width;
  min-width: $sidebar-width;
  height: 100vh;
  background-color: $bg-white;
  box-shadow: $box-shadow-light;
  transition: width 0.28s, min-width 0.28s;
  z-index: 1001;
  overflow-x: hidden;
  overflow-y: auto;
  flex-shrink: 0;

  &.sidebar-collapsed {
    width: $sidebar-collapsed-width;
    min-width: $sidebar-collapsed-width;
  }
}

.main-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: margin-left 0.28s;
}

.navbar {
  height: $header-height;
  background-color: $bg-white;
  box-shadow: $box-shadow-light;
  z-index: 1000;
  flex-shrink: 0;
}

.app-main {
  flex: 1;
  padding: $spacing-md;
  background-color: $bg-color;
  overflow-y: auto;
}

.watermark {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9999;
  background-repeat: repeat;
}

.sidebar-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1001;
}

// 手机端
.is-mobile {
  .sidebar-container {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    width: $sidebar-width;
    z-index: 1002;
    transition: transform 0.28s;

    &.sidebar-collapsed {
      width: $sidebar-width;
      transform: translateX(-100%);
    }
  }

  .main-container {
    width: 100%;
  }

  .app-main {
    padding: $spacing-sm;
  }
}
</style>
